import React, { useMemo } from 'react';
import CertificationTrendByCohortChart from './CertificationTrendByCohortChart';
import CertificationOutcomesByCohortChart from './CertificationOutcomesByCohortChart';
import OrganizationBreakdownCard from './OrganizationBreakdownCard';
import TopCountriesCertificationsCard from './TopCountriesCertificationsCard';
import BootcampPaginatedTableCard from './BootcampPaginatedTableCard';
import BootcampTablePaginationFooter from './BootcampTablePaginationFooter';
import { useBootcampTablePagination } from './useBootcampTablePagination';

const TABLE_PAGE_SIZE = 10;

const SHORT_COHORT_LABELS = {
  '30 Jun, 1 July, 3 July 2025': '30 Jun - 3 Jul',
  '7-9 July 2025': '7-9 Jul',
  '26-28 August 2025': '26-28 Aug',
  '16-18 September 2025': '16-18 Sep',
  '11-13 November 2025': '11-13 Nov',
  '20-21-22 January 2026': '20-22 Jan',
  'FY27Q1 - Self-Paced iLearn': 'FY27Q1 Self-Paced',
  'FY26Q4 - Self-Paced iLearn': 'FY26Q4 Self-Paced',
  'FY27Q2 - July 20-22': 'FY27Q2 Jul 20-22',
};

const STATUS_CLASS_MAP = {
  Completed: 'bootcamp-status-success',
  Incomplete: 'bootcamp-status-warning',
  'Missing Capstone': 'bootcamp-status-info',
  'Pending Capstone Review': 'bootcamp-status-review',
  'Pending Manager Review': 'bootcamp-status-review',
  Registered: 'bootcamp-status-registered',
  'No Show': 'bootcamp-status-muted',
  Failed: 'bootcamp-status-danger',
};

const REGION_ORDER = ['APJ', 'EMEA', 'NA', 'LATAM'];

const getShortCohortLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown Cohort';
  if (SHORT_COHORT_LABELS[raw]) return SHORT_COHORT_LABELS[raw];
  if (raw.length <= 18) return raw;
  return raw.replace(/\s+\d{4}$/g, '').slice(0, 22).trim();
};

const getRegionBadgeClass = (value) => {
  if (value === 'APJ') {
    return 'bootcamp-pill-region-apj';
  }
  if (value === 'EMEA') {
    return 'bootcamp-pill-region-emea';
  }
  if (value === 'NA') {
    return 'bootcamp-pill-region-na';
  }
  if (value === 'LATAM') {
    return 'bootcamp-pill-region-latam';
  }
  return '';
};

const getOrgBadgeClass = (value) => {
  if (value === 'Partner') {
    return 'bootcamp-pill-org-partner';
  }
  if (value === 'VSE') {
    return 'bootcamp-pill-org-vse';
  }
  if (value === 'Customer Success') {
    return 'bootcamp-pill-org-cs';
  }
  return '';
};

const ProgressList = ({ items, valueKey, totalKey, badgeKey, primaryLabelKey, mode }) => (
  <div className="bootcamp-progress-list">
    {items.map((item) => {
      const total = item[totalKey] || 0;
      const value = item[valueKey] || 0;
      const percentage = total ? Math.round((value / total) * 100) : 0;
      const badgeClass =
        mode === 'countries' ? getRegionBadgeClass(item[badgeKey]) : getOrgBadgeClass(item[badgeKey]);
      return (
        <div className="bootcamp-progress-row" key={`${item[primaryLabelKey]}-${item[badgeKey]}`}>
          <div className="bootcamp-progress-row-header">
            <div>
              <strong>{item[primaryLabelKey]}</strong>
              <span className={`bootcamp-pill ${badgeClass}`}>{item[badgeKey]}</span>
            </div>
            <span className="bootcamp-progress-value-pack">
              <span className="bootcamp-value-certified">{value}</span>
              <span className="bootcamp-value-total"> / {total}</span>
            </span>
          </div>
          <div className="bootcamp-progress-track">
            <span
              className={`bootcamp-progress-fill ${mode === 'companies' ? 'bootcamp-progress-fill-companies' : ''}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const RegionRateList = ({ items, getCompletionToneClass }) => (
  <div className="bootcamp-progress-list bootcamp-paired-scroll">
    {items.map((item) => {
      const completionRate = item.completionRate || 0;
      const certified = item.certified || 0;
      const total = item.total || 0;

      return (
        <div className="bootcamp-progress-row" key={item.region}>
          <div className="bootcamp-progress-row-header">
            <div>
              <strong>{item.region}</strong>
              <span className={`bootcamp-pill ${getRegionBadgeClass(item.region)}`}>{item.region}</span>
            </div>
            <span>
              <span className={`bootcamp-rate-pill ${getCompletionToneClass(completionRate)}`}>
                {completionRate}%
              </span>
              <span className="bootcamp-value-certified"> {certified}</span>
              <span className="bootcamp-value-total"> / {total}</span>
            </span>
          </div>
          <div className="bootcamp-progress-track">
            <span className="bootcamp-progress-fill" style={{ width: `${completionRate}%` }} />
          </div>
        </div>
      );
    })}
  </div>
);

const SectionHeader = ({ title, subtitle }) => (
  <div className="bootcamp-section-header">
    <div className="bootcamp-section-heading">
      <h2 className="bootcamp-section-title">{title}</h2>
      {subtitle ? <p className="bootcamp-section-subtitle">{subtitle}</p> : null}
    </div>
    <span className="bootcamp-section-divider" aria-hidden="true" />
  </div>
);

const getCountryRowTotal = (row) =>
  (row.total || 0) ||
  (row.certified || 0) +
    (row.missingCapstone || 0) +
    (row.pendingCapstoneReview || 0) +
    (row.registered || 0) +
    (row.failedNoShow || 0) +
    (row.pendingManagerReview || 0) +
    (row.incomplete || 0);

const BootcampOverviewTab = ({
  overviewData,
  isInitialLoading,
  isRefreshing,
  errorMessage,
  onViewAllParticipantStatuses,
}) => {
  const kpis = overviewData?.kpis || {};
  const outcomesByCohort = Array.isArray(overviewData?.certificationOutcomesByCohort)
    ? overviewData.certificationOutcomesByCohort
    : [];
  const organizationBreakdown = Array.isArray(overviewData?.organizationBreakdown)
    ? overviewData.organizationBreakdown
    : [];
  const topCountries = Array.isArray(overviewData?.topCountries) ? overviewData.topCountries.slice(0, 10) : [];
  const topCompanies = Array.isArray(overviewData?.topCompanies) ? overviewData.topCompanies.slice(0, 8) : [];
  const completionRateByCohort = Array.isArray(overviewData?.certificationTrendsByCohort)
    ? overviewData.certificationTrendsByCohort
    : [];
  const certificationRateByRegion = useMemo(() => {
    const regionData = Array.isArray(overviewData?.certificationRateByRegion)
      ? overviewData.certificationRateByRegion
      : [];
    const regionMap = regionData.reduce((acc, item) => {
      acc[item.region] = item;
      return acc;
    }, {});

    return REGION_ORDER.map((region) => regionMap[region]).filter(Boolean);
  }, [overviewData?.certificationRateByRegion]);
  const countryCertificationDetail = Array.isArray(overviewData?.countryCertificationDetail)
    ? overviewData.countryCertificationDetail
    : [];
  const companyScorecard = Array.isArray(overviewData?.companyCertificationScorecard)
    ? overviewData.companyCertificationScorecard
    : [];
  const championWorkload = Array.isArray(overviewData?.championFollowUpWorkload)
    ? overviewData.championFollowUpWorkload
    : [];
  const followUpRows = Array.isArray(overviewData?.participantsRequiringFollowUp)
    ? overviewData.participantsRequiringFollowUp.slice(0, 10)
    : [];
  const championWorkloadSorted = useMemo(
    () => [...championWorkload].sort((a, b) => (b.pendingAction || 0) - (a.pendingAction || 0)),
    [championWorkload]
  );

  const countriesTable = useBootcampTablePagination(countryCertificationDetail, {
    pageSize: TABLE_PAGE_SIZE,
    searchFields: ['country', 'region'],
  });

  const companiesTable = useBootcampTablePagination(companyScorecard, {
    pageSize: TABLE_PAGE_SIZE,
    searchFields: ['company', 'org'],
  });

  const championWorkloadTable = useBootcampTablePagination(championWorkloadSorted, {
    pageSize: TABLE_PAGE_SIZE,
    searchFields: [],
  });

  const getCompletionToneClass = (value) => {
    if (value >= 33) {
      return 'bootcamp-tone-strong';
    }
    if (value >= 26) {
      return 'bootcamp-tone-mid';
    }
    return 'bootcamp-tone-low';
  };

  if (isInitialLoading) {
    return (
      <div className="bootcamp-card bootcamp-table-card">
        <div className="bootcamp-muted-note">Loading Solution Demo Analytics overview...</div>
      </div>
    );
  }

  return (
    <div className={`bootcamp-overview ${isRefreshing ? 'bootcamp-overview-is-refreshing' : ''}`}>
      {isRefreshing && (
        <div className="bootcamp-refresh-banner" role="status" aria-live="polite">
          Updating analytics...
        </div>
      )}
      {errorMessage && (
        <div className="bootcamp-inline-error" role="alert">
          {errorMessage}
        </div>
      )}
      <div className="bootcamp-kpi-grid">
        <article className="bootcamp-kpi-card">
          <h4>Total Participants</h4>
          <p className="bootcamp-kpi-value">{kpis.totalParticipants}</p>
          <p className="bootcamp-kpi-subtext">
            {kpis.companiesCovered} companies · {kpis.countriesCovered} countries
          </p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Certified</h4>
          <p className="bootcamp-kpi-value">{kpis.certified}</p>
          <p className="bootcamp-kpi-subtext">{kpis.completionRate}% completion rate</p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Pending Capstone Review</h4>
          <p className="bootcamp-kpi-value">{kpis.pendingCapstoneReview || 0}</p>
          <p className="bootcamp-kpi-subtext">Awaiting review</p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Missing Capstone</h4>
          <p className="bootcamp-kpi-value">{kpis.missingCapstone}</p>
          <p className="bootcamp-kpi-subtext">Need final step</p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Failed / No Show</h4>
          <p className="bootcamp-kpi-value">{(kpis.failed || 0) + (kpis.noShow || 0)}</p>
          <p className="bootcamp-kpi-subtext">
            {kpis.failed || 0} failed · {kpis.noShow || 0} no show
          </p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Registered</h4>
          <p className="bootcamp-kpi-value">{kpis.registered}</p>
          <p className="bootcamp-kpi-subtext">Registered · not yet started</p>
        </article>

        <article className="bootcamp-kpi-card">
          <h4>Attendance Rate</h4>
          <p className="bootcamp-kpi-value">{kpis.attendanceRate}%</p>
          <p className="bootcamp-kpi-subtext">Attendance health</p>
        </article>
      </div>

      <section className="bootcamp-section">
        <SectionHeader title="Cohorts & Organization" />
        <div className="bootcamp-card-grid">
          <section id="export-certification-outcomes" className="bootcamp-card">
            <h3>Certification Outcomes by Cohort</h3>
            <CertificationOutcomesByCohortChart
              data={outcomesByCohort}
              getShortCohortLabel={getShortCohortLabel}
            />
          </section>

          <OrganizationBreakdownCard organizationBreakdown={organizationBreakdown} />
        </div>
      </section>

      <section className="bootcamp-section">
        <SectionHeader title="Countries" />
        <div className="bootcamp-card-grid bootcamp-card-grid-paired">
          <TopCountriesCertificationsCard countries={topCountries} />

          <BootcampPaginatedTableCard
            exportId="export-country-detail"
            title="All Countries · Certification Detail"
            countLabel={`${countriesTable.totalCount} countries`}
            searchPlaceholder="Search country or region"
            searchValue={countriesTable.search}
            onSearchChange={countriesTable.setSearch}
            page={countriesTable.page}
            totalPages={countriesTable.totalPages}
            onPageChange={countriesTable.setPage}
            isEmpty={countriesTable.isEmpty}
          >
            <div className="bootcamp-table-wrap bootcamp-table-wrap-compact bootcamp-analytics-table-wrap">
              <table className="bootcamp-table bootcamp-modern-table bootcamp-table-fixed bootcamp-table-tight">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Region</th>
                    <th>Done</th>
                    <th>Missing</th>
                    <th>Reg</th>
                    <th>Failed/NS</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {countriesTable.paginatedRows.map((row) => (
                    <tr key={`${row.country}-${row.region}`}>
                      <td>{row.country}</td>
                      <td>
                        <span className={`bootcamp-pill ${getRegionBadgeClass(row.region)}`}>{row.region}</span>
                      </td>
                      <td className="bootcamp-value-certified">{row.certified}</td>
                      <td className="bootcamp-value-missing">{row.missingCapstone}</td>
                      <td className="bootcamp-value-total">{row.registered || 0}</td>
                      <td className="bootcamp-value-failed">{row.failedNoShow}</td>
                      <td className="bootcamp-value-total">{getCountryRowTotal(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BootcampPaginatedTableCard>
        </div>
      </section>

      <section className="bootcamp-section">
        <SectionHeader title="Companies" />
        <div className="bootcamp-card-grid bootcamp-card-grid-paired">
          <section id="export-top-companies" className="bootcamp-card bootcamp-paired-card">
            <h3>Top Companies — Total Participants</h3>
            <div className="bootcamp-card-body bootcamp-paired-scroll">
              <ProgressList
                items={topCompanies}
                valueKey="certified"
                totalKey="totalParticipants"
                badgeKey="org"
                primaryLabelKey="company"
                mode="companies"
              />
            </div>
          </section>

          <BootcampPaginatedTableCard
            exportId="export-company-scorecard"
            title="Company Certification Scorecard"
            countLabel={`${companiesTable.totalCount} companies`}
            searchPlaceholder="Search company or org"
            searchValue={companiesTable.search}
            onSearchChange={companiesTable.setSearch}
            page={companiesTable.page}
            totalPages={companiesTable.totalPages}
            onPageChange={companiesTable.setPage}
            isEmpty={companiesTable.isEmpty}
            cardClassName="bootcamp-company-scorecard-card"
          >
            <div className="bootcamp-company-scorecard-wrap">
              <table className="bootcamp-table bootcamp-modern-table bootcamp-company-scorecard-table">
                <colgroup>
                  <col className="bootcamp-company-scorecard-col-company" />
                  <col className="bootcamp-company-scorecard-col-org" />
                  <col className="bootcamp-company-scorecard-col-num" />
                  <col className="bootcamp-company-scorecard-col-num" />
                  <col className="bootcamp-company-scorecard-col-num" />
                  <col className="bootcamp-company-scorecard-col-rate" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Org</th>
                    <th>Total</th>
                    <th>Done</th>
                    <th>Pending</th>
                    <th className="bootcamp-company-scorecard-rate-col">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {companiesTable.paginatedRows.map((row) => (
                    <tr key={row.company}>
                      <td title={row.company} className="bootcamp-cell-truncate">
                        {row.company}
                      </td>
                      <td>
                        <span className={`bootcamp-pill ${getOrgBadgeClass(row.org)}`}>{row.org}</span>
                      </td>
                      <td className="bootcamp-value-total">{row.participants}</td>
                      <td className="bootcamp-value-certified">{row.certified}</td>
                      <td className="bootcamp-value-pending">{row.pendingAction}</td>
                      <td className="bootcamp-company-scorecard-rate-cell">
                        <div className="bootcamp-rate-cell">
                          <div className="bootcamp-rate-progress">
                            <div className="bootcamp-progress-track">
                              <span
                                className="bootcamp-progress-fill"
                                style={{ width: `${row.completionRate}%` }}
                              />
                            </div>
                          </div>
                          <span
                            className={`bootcamp-rate-badge bootcamp-rate-pill ${getCompletionToneClass(row.completionRate)}`}
                          >
                            {row.completionRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </BootcampPaginatedTableCard>
        </div>
      </section>

      <section className="bootcamp-section">
        <SectionHeader title="Certification Progress" />
        <div className="bootcamp-card-grid">
          <section id="export-certification-rate-region" className="bootcamp-card">
            <h3>Certification Rate by Region</h3>
            <RegionRateList
              items={certificationRateByRegion}
              getCompletionToneClass={getCompletionToneClass}
            />
          </section>

          <section id="export-certification-trend-cohort" className="bootcamp-card bootcamp-cohort-trend-card">
            <h3>Certification Trend by Cohort</h3>
            <p className="bootcamp-muted-note bootcamp-cohort-trend-subtitle">
              Completion rate progression across cohorts
            </p>
            <CertificationTrendByCohortChart data={completionRateByCohort} />
          </section>
        </div>
      </section>

      <section className="bootcamp-section">
        <SectionHeader title="Follow-up" />
        <div className="bootcamp-card-grid">
          <section
            id="export-champion-workload"
            className="bootcamp-card bootcamp-table-card bootcamp-card-full-row bootcamp-champion-workload-card"
          >
            <div className="bootcamp-card-header-actions">
              <h3>Champion Follow-up Workload</h3>
              <span className="bootcamp-card-count">{championWorkloadTable.totalCount} champions</span>
            </div>
            <p className="bootcamp-muted-note bootcamp-champion-workload-subtitle">
              Top champions by pending follow-up
            </p>
            <div className="bootcamp-champion-table-wrapper bootcamp-analytics-table-wrap">
              <table className="bootcamp-table bootcamp-modern-table">
                <thead>
                  <tr>
                    <th>Champion</th>
                    <th>Participants</th>
                    <th>Completed</th>
                    <th>Pending</th>
                    <th>Completion Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {championWorkloadTable.paginatedRows.map((row) => (
                    <tr key={row.champion}>
                      <td title={row.champion} className="bootcamp-cell-truncate">
                        {row.champion}
                      </td>
                      <td className="bootcamp-value-total">{row.participants}</td>
                      <td className="bootcamp-value-certified">{row.completed}</td>
                      <td className="bootcamp-value-pending">{row.pendingAction}</td>
                      <td>
                        <div className="bootcamp-inline-progress">
                          <div className="bootcamp-progress-track">
                            <span
                              className="bootcamp-progress-fill"
                              style={{ width: `${row.completionRate}%` }}
                            />
                          </div>
                          <span className={`bootcamp-rate-pill ${getCompletionToneClass(row.completionRate)}`}>
                            {row.completionRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <BootcampTablePaginationFooter
              page={championWorkloadTable.page}
              totalPages={championWorkloadTable.totalPages}
              onPageChange={championWorkloadTable.setPage}
            />
          </section>

          <section
            id="export-participants-followup"
            className="bootcamp-card bootcamp-table-card bootcamp-table-no-scroll bootcamp-card-full-row"
          >
            <div className="bootcamp-card-header">
              <h3>Participants Requiring Follow-up</h3>
              <button type="button" className="bootcamp-link-button" onClick={onViewAllParticipantStatuses}>
                View all participant statuses →
              </button>
            </div>
            <div className="bootcamp-table-wrap bootcamp-table-wrap-compact bootcamp-analytics-table-wrap">
              <table className="bootcamp-table bootcamp-modern-table bootcamp-table-fixed">
                <thead>
                  <tr>
                    <th>Participant</th>
                    <th>Company</th>
                    <th>Region</th>
                    <th>Org</th>
                    <th>Champion</th>
                    <th>Certification Status</th>
                  </tr>
                </thead>
                <tbody>
                  {followUpRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.fullName}</td>
                      <td title={row.company} className="bootcamp-cell-truncate">
                        {row.company}
                      </td>
                      <td>{row.region}</td>
                      <td>{row.org}</td>
                      <td>{row.bmcHelixChampion}</td>
                      <td>
                        <span className={`bootcamp-status-pill ${STATUS_CLASS_MAP[row.certification] || ''}`}>
                          {row.certification}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};

export default BootcampOverviewTab;
